import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getUTM } from '../lib/utm';
import { track, posthogDistinctId } from '../lib/track';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  CheckCircle, 
  ArrowRight, 
  Gift,
  FileText,
  Target,
  BookOpen,
  Scale,
  MessageCircle,
  BarChart3,
  Shield,
  Calculator,
  PiggyBank,
  Clock,
  AlertTriangle,
  Zap,
  Star,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const SalesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPayment, setShowPayment] = useState(false);
  const [paymentStep, setPaymentStep] = useState('form'); // 'form', 'paypal', 'processing', 'success', 'error'
  const [formData, setFormData] = useState({ email: '', prenom: '', nom: '' });
  const [formErrors, setFormErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [annualPlanId, setAnnualPlanId] = useState('');
  const [monthlyPlanId, setMonthlyPlanId] = useState('');
  const [paypalClientId, setPaypalClientId] = useState(PAYPAL_CLIENT_ID);
  const [paypalMode, setPaypalMode] = useState('live');
  const [paypalBlocked, setPaypalBlocked] = useState(false);
  const [plan, setPlan] = useState(searchParams.get('plan') === 'monthly' ? 'monthly' : 'annual'); // 'annual' | 'monthly'
  const selectedPlanId = plan === 'annual' ? annualPlanId : monthlyPlanId;
  const isAnnual = plan === 'annual';
  const planLabel = isAnnual ? '299€/an' : '29,99€/mois';
  const paypalContainerRef = useRef(null);
  const paypalScriptLoaded = useRef(false);

  useEffect(() => {
    fetch(`${API_URL}/api/paypal/client-id`)
      .then(res => { if (res.status === 503) { setPaypalBlocked(true); return null; } return res.json(); })
      .then(d => {
        if (!d) return;
        setAnnualPlanId(d.annual_plan_id || ''); setMonthlyPlanId(d.monthly_plan_id || '');
        // LIVE : Client ID public du build (comportement historique) ; SANDBOX : Client ID sandbox fourni par le serveur
        if (d.mode === 'sandbox' && d.client_id) setPaypalClientId(d.client_id);
        else if (!PAYPAL_CLIENT_ID && d.client_id) setPaypalClientId(d.client_id);
        setPaypalMode(d.mode || 'live');
      })
      .catch(() => {});
  }, []);

  // Arrivée avec une formule choisie : afficher directement le bloc de souscription ; sinon haut de page
  useEffect(() => {
    const p = searchParams.get('plan');
    const t = setTimeout(() => {
      const card = (p === 'monthly' || p === 'annual') ? document.querySelector('[data-testid="plan-selector"]') : null;
      window.scrollTo({ top: card ? card.getBoundingClientRect().top + window.scrollY - 84 : 0 });
    }, 50);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pendant le paiement, la bannière cookies est masquée (sans enregistrer de choix) pour ne couvrir aucun bouton
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('cp-checkout-active', { detail: showPayment }));
    return () => window.dispatchEvent(new CustomEvent('cp-checkout-active', { detail: false }));
  }, [showPayment]);

  // Load PayPal SDK (mode abonnement)
  useEffect(() => {
    if (paymentStep === 'paypal' && selectedPlanId && !paypalScriptLoaded.current) {
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&currency=EUR&intent=subscription&vault=true`;
      script.async = true;
      script.onload = () => {
        paypalScriptLoaded.current = true;
        renderPayPalButton();
      };
      document.body.appendChild(script);
    } else if (paymentStep === 'paypal' && selectedPlanId && paypalScriptLoaded.current) {
      renderPayPalButton();
    }
  }, [paymentStep, selectedPlanId]);

  const renderPayPalButton = () => {
    if (!window.paypal || !paypalContainerRef.current) return;
    
    // Clear previous button safely
    while (paypalContainerRef.current.firstChild) {
      paypalContainerRef.current.removeChild(paypalContainerRef.current.firstChild);
    }
    
    window.paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'gold',
        shape: 'rect',
        label: 'paypal',
        height: 50
      },
      createSubscription: (data, actions) => {
        return actions.subscription.create({ plan_id: selectedPlanId });
      },
      onApprove: async (data) => {
        setPaymentStep('processing');
        try {
          const response = await fetch(`${API_URL}/api/paypal/activate-subscription`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              subscription_id: data.subscriptionID,
              ph_distinct_id: posthogDistinctId(),
              email: formData.email,
              prenom: formData.prenom,
              nom: formData.nom,
              utm: getUTM() || undefined
            })
          });
          
          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || "Erreur lors de l'activation de l'abonnement");
          }
          
          const result = await response.json();
          
          // Auth via httpOnly cookie set by backend — redirection vers la page de confirmation
          navigate('/merci-annuel', {
            state: {
              prenom: formData.prenom,
              email: formData.email,
              accessLinkSent: !!result.access_link_sent,
              requiresLogin: !!result.requires_login,
              plan
            }
          });
          
        } catch (error) {
          // Error handled by toast
          setErrorMessage(error.message);
          setPaymentStep('error');
        }
      },
      onError: (err) => {
        // Error handled by toast
        setErrorMessage('Une erreur est survenue avec PayPal. Veuillez réessayer.');
        setPaymentStep('error');
      },
      onCancel: () => {
        setPaymentStep('paypal');
      }
    }).render(paypalContainerRef.current);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email) {
      errors.email = 'Email requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email invalide';
    }
    if (!formData.prenom) errors.prenom = 'Prénom requis';
    if (!formData.nom) errors.nom = 'Nom requis';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      track('mon_ae_checkout_started', { plan, location: 'achat' });
      setPaymentStep('paypal');
    }
  };

  // 9 bonus (valeur totale: 993€)
  const bonusList = [
    { icon: BookOpen, title: 'Guide Complet : Quel Statut Après l\'Auto-Entreprise ?', value: 97, description: 'EI, EURL, SASU, SARL... Comparatif détaillé avec simulations chiffrées 2026' },
    { icon: Shield, title: 'Guide Retraite & Prévoyance de l\'Auto-Entrepreneur', value: 147, description: 'Trimestres, pension, PER, arrêt maladie, prévoyance - Tout pour protéger votre avenir' },
    { icon: FileText, title: 'Pack Modèles de Courriers & Relances', value: 97, description: '12 modèles professionnels : relances clients, devis, courriers URSSAF et Impôts' },
    { icon: Scale, title: 'Guide BIC vs BNC : Tout Comprendre', value: 67, description: 'Plafonds, taux de cotisations, abattements fiscaux, versement libératoire - Le guide complet' },
    { icon: BarChart3, title: 'Atelier prise en main du Dashboard', value: 97, description: 'Guide complet pour maîtriser votre tableau de bord en 30 minutes' },
    { icon: Target, title: 'Atelier prévisionnel \'Objectif 1K/mois\'', value: 147, description: 'Votre feuille de route pour atteindre 1000€ net par mois' },
    { icon: Gift, title: 'Kit juridique : CGV, devis, mentions légales', value: 97, description: '6 modèles prêts à l\'emploi, à jour 2026' },
    { icon: Calculator, title: 'Étude personnelle PER et Prévoyance', value: 147, description: 'Simulateurs et conseils pour comprendre le PER et la prévoyance et protéger votre avenir' },
    { icon: FileText, title: 'Guide Facture Électronique 2026', value: 97, description: 'Tout savoir sur l\'obligation de facturation électronique : calendrier, outils et mise en conformité' },
  ];

  const features = [
    { 
      icon: BarChart3, 
      title: 'Dashboard complet', 
      items: [
        'Vue d\'ensemble de votre activité en temps réel',
        'Graphiques CA et factures mensuels',
        'Checklist mensuelle automatique',
        'Alertes plafonds CA et échéances'
      ] 
    },
    { 
      icon: FileText, 
      title: 'Gestion documentaire', 
      items: [
        'Création de factures en 2 clics',
        'Gestion des devis avec suivi',
        'Transformation devis → facture automatique',
        'Suivi des paiements et relances'
      ] 
    },
    { 
      icon: Calculator, 
      title: 'Outils fiscaux', 
      items: [
        'Simulateur TVA (collectée vs déductible)',
        'Calcul cotisations URSSAF en temps réel',
        'Taux 2026 mis à jour automatiquement',
        'Alertes dépassement de plafonds'
      ] 
    },
    { 
      icon: Shield, 
      title: 'Protection & prévoyance', 
      items: [
        'Simulateur protection sociale détaillé',
        'Comparatif régimes (libéral vs artisan)',
        'Délais de carence expliqués (90j vs 3j)',
        'Checklist assurance personnalisée'
      ] 
    },
    { 
      icon: PiggyBank, 
      title: 'Épargne retraite', 
      items: [
        'Comparatif retraite AE vs EI (chiffres réels)',
        'Calcul épargne recommandée selon âge',
        'Projection de capital à 65 ans',
        'Stratégies de compensation détaillées'
      ] 
    },
    { 
      icon: Clock, 
      title: 'Gain de temps', 
      items: [
        'Tout centralisé au même endroit',
        'Plus de tableaux Excel compliqués',
        'Rappels automatiques',
        '~10h gagnées par mois'
      ] 
    },
  ];

  const totalBonusValue = bonusList.reduce((sum, bonus) => sum + bonus.value, 0);
  const planPrice = isAnnual ? 299 : 29.99;
  const totalValue = planPrice + totalBonusValue; // Prix dashboard + bonus

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="font-bold text-xl font-['Manrope']">
            <span className="text-primary">MON AE</span> <span className="text-sm font-medium text-muted-foreground">par Capsule Patrimoine</span>
          </Link>
          <Link to="/login">
            <Button variant="ghost" data-testid="sales-login-btn">Connexion</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge prix */}
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Zap className="h-4 w-4" />
            Deux formules : 29,99€/mois ou 299€/an
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Manrope'] tracking-tight mb-6">
            <span className="gradient-text">MON AE</span>, le tableau de bord complet des auto-entrepreneurs
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Tout ce dont vous avez besoin pour gérer votre activité sereinement, 
            de la facturation à la retraite. <strong>Moins de 25€ par mois, zéro prise de tête.</strong>
          </p>
        </div>
      </section>

      {/* Problème */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-red-50">
        <div className="max-w-4xl mx-auto">
          <Card className="border-red-200 bg-white">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="h-7 w-7 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold font-['Manrope'] text-red-900 mb-4">
                    Les 3 erreurs qui coûtent cher aux auto-entrepreneurs
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold">1.</span>
                      <span><strong>Ignorer sa protection sociale</strong> : 90 jours sans revenus en cas d'arrêt maladie (professions libérales)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold">2.</span>
                      <span><strong>Ne pas anticiper sa retraite</strong> : 400-600€/mois vs 1200-1500€/mois en EI classique</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 font-bold">3.</span>
                      <span><strong>Gérer au jour le jour</strong> : Surprises URSSAF, TVA oubliée, plafonds dépassés</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold font-['Manrope'] text-center mb-4">
            Ce que contient MON AE
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Un outil ultra-complet + une Patrithèque exclusive pour ne plus jamais être pris au dépourvu
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <Card key={feature.title} className="card-hover">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-bold font-['Manrope'] text-xl mb-4">{feature.title}</h3>
                  <ul className="space-y-2">
                    {feature.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Exemple comparatif retraite */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold font-['Manrope'] text-center mb-4">
            La vérité sur votre retraite
          </h2>
          <p className="text-center text-muted-foreground mb-8">
            Exemple : 25 ans de cotisation avec un CA moyen de 2 000 €/mois
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-red-200">
              <CardContent className="p-6 text-center">
                <h4 className="font-semibold text-red-900 mb-2">Auto-entrepreneur</h4>
                <p className="text-4xl font-bold font-['Manrope'] text-red-600 mb-2">400 - 600 €/mois</p>
                <p className="text-sm text-muted-foreground">Pension estimée</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200">
              <CardContent className="p-6 text-center">
                <h4 className="font-semibold text-emerald-900 mb-2">Entreprise individuelle</h4>
                <p className="text-4xl font-bold font-['Manrope'] text-emerald-600 mb-2">1 200 - 1 500 €/mois</p>
                <p className="text-sm text-muted-foreground">Pension estimée</p>
              </CardContent>
            </Card>
          </div>
          
          <p className="text-center mt-6 text-red-600 font-medium">
            Différence : 600 à 900 €/mois • Soit 144 000 à 216 000 € sur 20 ans de retraite
          </p>
          <p className="text-center mt-2 text-sm text-muted-foreground">
            Notre outil vous aide à anticiper et compenser ce déficit.
          </p>
        </div>
      </section>

      {/* Patrithèque Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-secondary/20 text-secondary-foreground px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Gift className="h-4 w-4" />
              9 ressources Patrithèque incluses
            </div>
            <h2 className="text-3xl font-bold font-['Manrope'] mb-4">
              Ce que tu reçois en plus du Dashboard
            </h2>
            <p className="text-muted-foreground">
              Valeur totale de la Patrithèque : <span className="font-bold text-foreground">{totalBonusValue}€</span>
            </p>
          </div>

          <div className="space-y-4">
            {bonusList.map((bonus, index) => (
              <Card key={bonus.title} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-secondary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <bonus.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-primary">Ressource #{index + 1}</span>
                        </div>
                        <h4 className="font-semibold font-['Manrope']">{bonus.title}</h4>
                        <p className="text-sm text-muted-foreground">{bonus.description}</p>
                      </div>
                    </div>
                    <div className="text-lg font-bold text-muted-foreground whitespace-nowrap">
                      {bonus.value}€
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <Card className="overflow-hidden shadow-2xl" data-testid="pricing-card">
            <CardContent className="p-0">
              <div className="bg-primary text-primary-foreground p-8 text-center relative overflow-hidden">
                {isAnnual && (
                  <div className="absolute top-0 right-0 bg-red-500 text-white px-4 py-1 text-sm font-bold transform rotate-12 translate-x-2 -translate-y-2">
                    2 MOIS OFFERTS
                  </div>
                )}
                <h2 className="text-2xl font-bold font-['Manrope'] mb-2">Abonnement MON AE Auto-Entrepreneur</h2>
                <p className="opacity-90">Accès complet + Mises à jour incluses · Mensuel ou annuel, mêmes fonctionnalités</p>
              </div>
              
              <div className="p-8">
                {/* Sélecteur de formule */}
                <div className="flex justify-center mb-6">
                  <div className="inline-flex rounded-full border-2 border-primary/30 bg-muted/40 p-1" role="tablist" data-testid="plan-selector">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={!isAnnual}
                      data-testid="plan-monthly-btn"
                      onClick={() => { setPlan('monthly'); setShowPayment(false); setPaymentStep('form'); track('mon_ae_monthly_click', { plan: 'monthly', location: 'achat-selector' }); }}
                      className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${!isAnnual ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      Mensuel · 29,99€/mois
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isAnnual}
                      data-testid="plan-annual-btn"
                      onClick={() => { setPlan('annual'); setShowPayment(false); setPaymentStep('form'); track('mon_ae_annual_click', { plan: 'annual', location: 'achat-selector' }); }}
                      className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${isAnnual ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      Annuel · 299€/an
                    </button>
                  </div>
                </div>

                <div className="text-center mb-8">
                  <p className="text-sm text-red-600 font-medium mb-4" data-testid="plan-selected-label">{isAnnual ? 'Abonnement annuel' : 'Abonnement mensuel sans engagement'}</p>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-6xl font-bold font-['Manrope'] text-primary" data-testid="plan-price">{isAnnual ? '299€' : '29,99€'}</span>
                    <span className="text-2xl text-muted-foreground font-semibold">{isAnnual ? '/an' : '/mois'}</span>
                  </div>
                  <p className="text-muted-foreground mt-4">
                    {isAnnual
                      ? <>soit <span className="font-semibold text-foreground">24,92€/mois</span> — 2 mois offerts vs l&apos;abonnement mensuel (29,99€/mois)</>
                      : <>Sans engagement · résiliable à tout moment, avec prise d&apos;effet à l&apos;issue de la période mensuelle en cours</>}
                  </p>
                </div>

                {/* Récap valeur */}
                <div className="bg-muted/50 rounded-lg p-4 mb-6">
                  <div className="flex justify-between mb-2">
                    <span>{isAnnual ? 'Dashboard complet (12 mois)' : 'Dashboard complet (1 mois, renouvelé chaque mois)'}</span>
                    <span className="font-medium">{isAnnual ? '299€' : '29,99€'}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>9 ressources Patrithèque exclusives</span>
                    <span className="font-medium">{totalBonusValue}€</span>
                  </div>
                  <div className="border-t pt-2 mt-2 flex justify-between">
                    <span className="font-bold">Valeur totale</span>
                    <span className="font-bold">{isAnnual ? totalValue : totalValue.toFixed(2).replace('.', ',')}€</span>
                  </div>
                  <div className="flex justify-between text-primary">
                    <span className="font-bold">Vous payez</span>
                    <span className="font-bold text-xl" data-testid="plan-you-pay">{planLabel}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  {!showPayment ? (
                    <Button 
                      size="lg" 
                      className="w-full text-lg py-6"
                      data-testid="buy-now-btn"
                      onClick={() => { setShowPayment(true); track(isAnnual ? 'mon_ae_annual_click' : 'mon_ae_monthly_click', { plan, location: 'achat-buy' }); }}
                    >
                      Je m&apos;abonne — {planLabel}
                      <ArrowRight className="h-5 w-5 ml-2" />
                    </Button>
                  ) : (
                    <div className="space-y-4 p-6 border-2 border-primary rounded-lg bg-white">
                      
                      {/* Step 1: Form */}
                      {paymentStep === 'form' && (
                        <>
                          <h3 className="font-semibold text-center text-lg">Vos informations</h3>
                          <p className="text-center text-sm text-muted-foreground">
                            Ces informations serviront à créer votre compte
                          </p>
                          <form onSubmit={handleFormSubmit} className="space-y-4">
                            <div>
                              <Label htmlFor="email">Email *</Label>
                              <Input 
                                id="email"
                                type="email"
                                placeholder="votre@email.com"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                className={formErrors.email ? 'border-red-500' : ''}
                                data-testid="payment-email-input"
                              />
                              {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="prenom">Prénom *</Label>
                                <Input 
                                  id="prenom"
                                  placeholder="Jean"
                                  value={formData.prenom}
                                  onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                                  className={formErrors.prenom ? 'border-red-500' : ''}
                                  data-testid="payment-prenom-input"
                                />
                                {formErrors.prenom && <p className="text-red-500 text-xs mt-1">{formErrors.prenom}</p>}
                              </div>
                              <div>
                                <Label htmlFor="nom">Nom *</Label>
                                <Input 
                                  id="nom"
                                  placeholder="Dupont"
                                  value={formData.nom}
                                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                                  className={formErrors.nom ? 'border-red-500' : ''}
                                  data-testid="payment-nom-input"
                                />
                                {formErrors.nom && <p className="text-red-500 text-xs mt-1">{formErrors.nom}</p>}
                              </div>
                            </div>
                            <Button type="submit" className="w-full" data-testid="continue-to-payment-btn">
                              Continuer vers le paiement
                              <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                          </form>
                        </>
                      )}

                      {/* Step 2: PayPal Button */}
                      {paymentStep === 'paypal' && (
                        <>
                          <h3 className="font-semibold text-center text-lg">Finalisez votre abonnement</h3>
                          <p className="text-center text-sm text-muted-foreground">
                            Paiement sécurisé par PayPal (CB ou compte PayPal)
                          </p>
                          {paypalMode === 'sandbox' && (
                            <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900 font-semibold text-center" data-testid="paypal-sandbox-banner">
                              MODE TEST PAYPAL SANDBOX — aucun paiement réel ne sera effectué
                            </div>
                          )}
                          {paypalBlocked && (
                            <div className="rounded-lg border-2 border-red-400 bg-red-50 p-3 text-sm text-red-800 text-center" data-testid="paypal-blocked-banner">
                              Paiement temporairement indisponible. Merci de réessayer dans quelques minutes ou de nous écrire à contact@capsulepatrimoine.com.
                            </div>
                          )}
                          <div className="bg-muted/50 rounded-lg p-3 text-sm">
                            <p><strong>Email :</strong> {formData.email}</p>
                            <p><strong>Nom :</strong> {formData.prenom} {formData.nom}</p>
                          </div>
                          
                          {/* PayPal SDK Button Container */}
                          {!paypalBlocked && (
                          <div 
                            ref={paypalContainerRef}
                            id="paypal-button-container"
                            className="min-h-[150px]"
                            data-testid="paypal-button-container"
                          />
                          )}

                          <button 
                            onClick={() => setPaymentStep('form')}
                            className="text-sm text-muted-foreground hover:underline w-full text-center"
                          >
                            ← Modifier mes informations
                          </button>
                        </>
                      )}

                      {/* Step 3: Processing */}
                      {paymentStep === 'processing' && (
                        <div className="text-center py-8">
                          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                          <h3 className="font-semibold text-lg mb-2">Traitement en cours...</h3>
                          <p className="text-sm text-muted-foreground">
                            Nous finalisons votre paiement et créons votre compte.
                          </p>
                        </div>
                      )}

                      {/* Step 4: Success */}
                      {paymentStep === 'success' && (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="h-8 w-8 text-emerald-600" />
                          </div>
                          <h3 className="font-semibold text-lg text-emerald-700 mb-2">Paiement réussi !</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            Votre compte a été créé avec succès.
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Redirection vers votre dashboard dans quelques secondes...
                          </p>
                          <Button 
                            className="mt-4"
                            onClick={() => navigate('/dashboard')}
                            data-testid="go-to-dashboard-btn"
                          >
                            Accéder au Dashboard maintenant
                          </Button>
                        </div>
                      )}

                      {/* Step 5: Error */}
                      {paymentStep === 'error' && (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertTriangle className="h-8 w-8 text-red-600" />
                          </div>
                          <h3 className="font-semibold text-lg text-red-700 mb-2">Une erreur est survenue</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            {errorMessage || 'Le paiement n\'a pas pu être traité.'}
                          </p>
                          <Button 
                            onClick={() => {
                              setErrorMessage('');
                              setPaymentStep('form');
                            }}
                            data-testid="retry-payment-btn"
                          >
                            Réessayer
                          </Button>
                        </div>
                      )}

                      {(paymentStep === 'form' || paymentStep === 'paypal') && (
                        <button 
                          onClick={() => {
                            setShowPayment(false);
                            setPaymentStep('form');
                          }}
                          className="text-sm text-muted-foreground hover:underline w-full text-center"
                        >
                          ← Retour
                        </button>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      Paiement sécurisé
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      Satisfait ou remboursé 14 jours
                    </span>
                  </div>
                </div>

                <div className="space-y-3 mt-8" data-testid="plan-features-list">
                  {[
                    isAnnual ? 'Accès complet au Dashboard pendant 12 mois' : 'Accès complet au Dashboard, renouvelé chaque mois',
                    'Tous les simulateurs (URSSAF, TVA, Retraite, Protection sociale)',
                    'Gestion factures & devis complète',
                    'Suivi des dépenses par catégorie',
                    '9 ressources Patrithèque exclusives (valeur ' + totalBonusValue + '€)',
                    'Mises à jour automatiques des taux',
                    'Support par email',
                    'Comparatif retraite AE vs EI détaillé'
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Garantie */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="h-10 w-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold font-['Manrope'] mb-4">
            Garantie Satisfait ou Remboursé 14 jours
          </h2>
          <p className="text-muted-foreground">
            Testez MON AE pendant 14 jours. Si vous n'êtes pas satisfait, 
            nous vous remboursons intégralement, sans question.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold font-['Manrope'] text-center mb-12">
            Questions fréquentes
          </h2>
          
          <div className="space-y-6">
            {[
              {
                q: 'Les taux URSSAF et TVA sont-ils à jour ?',
                a: 'Oui ! Les taux URSSAF 2026 sont intégrés (BNC 25,6 %, BIC 12,3 % et 21,2 %, CIPAV 23,2 %) ainsi que les plafonds 2026. Ils sont mis à jour à chaque évolution réglementaire, et vous pouvez aussi les ajuster vous-même.'
              },
              {
                q: 'Est-ce adapté à mon type d\'activité ?',
                a: 'Oui, MON AE est conçu pour tous les types d\'auto-entreprises : BIC (vente), BIC (services) et BNC (libéral). Les simulateurs prennent en compte les spécificités de chaque régime.'
              },
              {
                q: 'Le comparatif retraite est-il fiable ?',
                a: 'Les chiffres sont basés sur les règles de calcul actuelles. C\'est une estimation pour vous aider à anticiper, pas un engagement de l\'administration.'
              },
              {
                q: 'Puis-je résilier mon abonnement annuel ?',
                a: 'Oui. L\'abonnement est renouvelé automatiquement chaque année, mais vous pouvez le résilier à tout moment avant l\'échéance depuis votre compte PayPal ou par simple email. Et vous bénéficiez de la garantie satisfait ou remboursé de 14 jours.'
              },
              {
                q: 'Pourquoi choisir l\'annuel plutôt que le mensuel ?',
                a: '299€/an revient à 24,92€/mois, contre 29,99€/mois en formule mensuelle : vous économisez l\'équivalent de 2 mois chaque année, avec exactement les mêmes fonctionnalités.'
              },
              {
                q: 'Comment accéder à la Patrithèque ?',
                a: 'Toutes les ressources de la Patrithèque sont accessibles directement depuis votre espace membre, sous forme de guides interactifs en ligne.'
              }
            ].map((faq) => (
              <Card key={faq.q}>
                <CardContent className="p-6">
                  <h4 className="font-semibold font-['Manrope'] mb-2">{faq.q}</h4>
                  <p className="text-muted-foreground">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <Star className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl sm:text-4xl font-bold font-['Manrope'] mb-6">
            Investissez dans votre sérénité
          </h2>
          <p className="text-lg opacity-90 mb-4 max-w-2xl mx-auto">
            Pour 29,99€/mois ou 299€/an, vous gagnez des heures de gestion chaque mois 
            et vous sécurisez votre avenir d'auto-entrepreneur.
          </p>
          <p className="text-sm opacity-75 mb-8">
            Garantie satisfait ou remboursé 14 jours
          </p>
          <Button 
            size="lg" 
            variant="secondary"
            className="font-semibold"
            data-testid="final-cta-btn"
            onClick={() => document.querySelector('[data-testid="pricing-card"]')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Je m&apos;abonne — {planLabel}
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="font-bold font-['Manrope'] text-lg mb-4">
                <span className="text-primary">MON AE</span> · Capsule Patrimoine
              </div>
              <p className="text-sm text-muted-foreground">
                L'outil tout-en-un pour gérer votre auto-entreprise sereinement.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Liens rapides</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Webinaire gratuit</Link></li>
                <li><Link to="/login" className="hover:text-primary">Connexion</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>contact@capsulepatrimoine.com</li>
                <li>07 44 37 09 40</li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t text-center text-xs text-muted-foreground space-y-2">
            <p>© 2026 Capsule Patrimoine - Tous droits réservés</p>
            <p>SIRET : 484 280 771 00060 | Tribunal compétent : Roanne (Loire)</p>
            <p>Mentions légales | CGV | Politique de confidentialité</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SalesPage;
